import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cookies from "./cookies";
import Config from "../config";
import { notify } from "../components/main/notify";

const TIMEOUT_MS = 180000;

function createAbortController() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
    return { signal: controller.signal, clearTimeout: () => clearTimeout(timeoutId) };
}

function isAbortError(e: unknown): boolean {
    return e instanceof DOMException && e.name === "AbortError";
}

function getErrorMessage(e: unknown, timeoutMsg: string, fallbackMsg: string): string {
    if (isAbortError(e)) return timeoutMsg;
    return (e as any)?.message || fallbackMsg;
}

function buildHeaders(cookie: Cookies, extra: Record<string, string> = {}): HeadersInit {
    return {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: (cookie.get("token") as string) ?? "",
        "X-Access-Token": (cookie.get("access_token") as string) ?? "",
        ...extra,
    };
}

async function parseResponse(res: Response) {
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw Object.assign(new Error(err?.message || "Error, please try again."), { responseData: err });
    }
    return res.json();
}

export default class fetch {
    path?: string | null;
    data?: object;
    notify_: {
        success: boolean;
        error: boolean;
    };

    constructor(path?: string | null, data?: object, notify_ = { success: false, error: false }) {
        this.path = path;
        this.data = data;
        this.notify_ = notify_;
    }

    private resolveUrl(): string {
        const api = new Config().env().api;
        return this.path?.startsWith("http") ? this.path : api + this.path;
    }

    private handleAuthTokens(data: any, cookie: Cookies) {
        if (data?.auth?.token) cookie.set("token", data.auth.token, 999);
        if (data?.auth?.access_token) cookie.set("access_token", data.auth.access_token, 999);
    }

    private handleToast(data: any) {
        if (this.notify_.success && data.status === "success") {
            toast.success(data.message, { isLoading: false, position: "top-center", autoClose: 5000, closeOnClick: true });
        } else if (this.notify_.error && data.status === "error") {
            toast.error(data.message, { isLoading: false, position: "top-center", autoClose: 5000, closeOnClick: true });
        }
    }

    async get() {
        const cookie = new Cookies();
        const { signal, clearTimeout } = createAbortController();
        const url = new URL(this.resolveUrl());

        if (this.data) {
            Object.entries(this.data).forEach(([k, v]) => url.searchParams.append(k, String(v)));
        }

        try {
            const res = await window.fetch(url.toString(), {
                method: "GET",
                signal,
                headers: buildHeaders(cookie),
            });
            clearTimeout();

            const data = await parseResponse(res);

            if (data.redirect) {
                window.location.href = data.redirect;
                return false;
            }
            if (data.status === "success") this.handleAuthTokens(data, cookie);
            this.handleToast(data);
            return data;
        } catch (e) {
            clearTimeout();
            return false; // Never let this throw
        }
    }

    async post() {
        const cookie = new Cookies();
        const { signal, clearTimeout } = createAbortController();

        try {
            const res = await window.fetch(this.resolveUrl(), {
                method: "POST",
                signal,
                headers: buildHeaders(cookie),
                body: JSON.stringify(this.data),
            });
            clearTimeout();

            const data = await parseResponse(res);

            if (data.redirect) {
                window.location.href = data.redirect;
                return false;
            }
            if (data.status === "success") this.handleAuthTokens(data, cookie);
            this.handleToast(data);
            return data;
        } catch (e) {
            clearTimeout();
            const message = getErrorMessage(e, "Request timed out. Please check your connection.", "Error, please try again.");
            try {
                toast.error(message, { isLoading: false, position: "top-center", autoClose: 5000, closeOnClick: true });
            } catch (_) {
                console.error("Toast failed:", message);
            }
            return false; // Never let this throw
        }
    }

    async post_file() {
        const cookie = new Cookies();
        const { signal, clearTimeout } = createAbortController();

        try {
            const res = await window.fetch(this.resolveUrl(), {
                method: "POST",
                signal,
                headers: {
                    Authorization: (cookie.get("token") as string) ?? "",
                    "X-Access-Token": (cookie.get("access_token") as string) ?? "",
                    // ⚠️ No Content-Type — browser sets it automatically with the correct boundary for FormData
                },
                body: this.data as FormData,
            });
            clearTimeout();

            const data = await parseResponse(res);

            if (data.redirect) {
                window.location.href = data.redirect;
                return false;
            }
            if (data.status === "success") this.handleAuthTokens(data, cookie);
            this.handleToast(data);
            return data;
        } catch (e) {
            clearTimeout();
            const message = getErrorMessage(e, "Request timed out. Please check your connection.", "Error, please try again.");
            try {
                toast.error(message, { isLoading: false, position: "top-center", autoClose: 5000, closeOnClick: true });
            } catch (_) {
                console.error("Toast failed:", message);
            }
            return false; // Never let this throw
        }
    }

    async auth() {
        const cookie = new Cookies();
        const { signal, clearTimeout } = createAbortController();
        const api = new Config().env().api;

        try {
            const res = await window.fetch(api + "/auth", {
                method: "GET",
                signal,
                headers: buildHeaders(cookie),
            });
            clearTimeout();

            const data = await parseResponse(res);

            if (data.redirect) {
                window.location.href = data.redirect;
                return false;
            }
            if (data.status === "success") this.handleAuthTokens(data, cookie);
            if ((this.notify_.success && data.status === "success") || (this.notify_.error && data.status === "error")) {
                notify(data.status, data.message);
            }
            return data;
        } catch (e) {
            clearTimeout();
            const message = getErrorMessage(e, "Request timed out. Please check your connection.", "Error, please try again.");
            try {
                toast.error(message, { isLoading: false, position: "top-center", autoClose: 5000, closeOnClick: true });
            } catch (_) {
                console.error("Toast failed:", message);
            }
            return false; // Never let this throw
        }
    }
}
